/**
 * Security Initialization
 * Sets up security services when the application starts
 */

import { cspService } from './csp';
import { rateLimiter } from './rate-limiter';
import { secureApiClient } from './secure-api-client';
import { errorMonitoringService } from '../monitoring/error-monitoring';

/**
 * Initialize all security services
 */
export function initializeSecurity(): void {
  // Determine environment - use VITE_ENV if available, otherwise check PROD flag
  const viteEnv = import.meta.env.VITE_ENV;
  const environment = viteEnv === 'production' || viteEnv === 'staging'
    ? 'production'
    : 'development';

  console.log(`Initializing security services for ${environment} environment (VITE_ENV: ${viteEnv})...`);

  // 1. Initialize CSP (temporarily disabled for debugging)
  try {
    // cspService.refreshNonce();
    // cspService.applyToDocument(environment);
    // cspService.setupViolationReporting();
    console.log('✓ Content Security Policy (disabled for debugging)');
  } catch (error) {
    console.error('Failed to initialize CSP:', error);
  }

  // 2. Initialize rate limiter
  try {
    // Rate limiter is automatically initialized as singleton
    // Just verify it's working
    const testResult = rateLimiter.checkLimit({
      endpoint: '/test',
      method: 'GET',
      timestamp: Date.now()
    });
    console.log('✓ Rate limiter initialized');
  } catch (error) {
    console.error('Failed to initialize rate limiter:', error);
  }

  // 3. Initialize secure API client
  try {
    // Secure API client is initialized with default config
    const config = secureApiClient.getConfig();
    console.log('✓ Secure API client initialized with config:', config);
  } catch (error) {
    console.error('Failed to initialize secure API client:', error);
  }

  // 4. Set up global error handlers
  setupGlobalErrorHandlers();

  // 5. Set up security monitoring
  setupSecurityMonitoring();

  // 6. Initialize PostHog monitoring
  try {
    errorMonitoringService.initialize();
    console.log('✓ PostHog analytics initialized');
  } catch (error) {
    console.error('Failed to initialize PostHog:', error);
  }

  console.log('🔒 Security initialization complete');
}

/**
 * Set up global error handlers for security
 */
function setupGlobalErrorHandlers(): void {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.type === 'security_violation') {
      console.warn('Security violation detected:', event.reason.violation);
      // Prevent the default browser console error
      event.preventDefault();
    }
  });

  // Handle global errors
  window.addEventListener('error', (event) => {
    // Check if it's a security-related error
    if (event.error?.type === 'security_violation') {
      console.warn('Security error caught:', event.error.violation);
    }
  });

  // Handle CSP violations
  document.addEventListener('securitypolicyviolation', (event) => {
    console.warn('CSP violation detected:', {
      blockedURI: event.blockedURI,
      violatedDirective: event.violatedDirective,
      originalPolicy: event.originalPolicy,
      sourceFile: event.sourceFile,
      lineNumber: event.lineNumber
    });

    // Report to CSP service
    cspService.reportViolation({
      documentURI: event.documentURI,
      referrer: event.referrer,
      blockedURI: event.blockedURI,
      violatedDirective: event.violatedDirective,
      originalPolicy: event.originalPolicy,
      sourceFile: event.sourceFile,
      lineNumber: event.lineNumber,
      columnNumber: event.columnNumber
    });
  });
}

/**
 * Set up security monitoring and reporting
 */
function setupSecurityMonitoring(): void {
  // Monitor rate limit violations
  setInterval(() => {
    const violations = secureApiClient.getViolationsByType('rate_limit');
    if (violations.length > 0) {
      console.log(`Rate limit violations in last period: ${violations.length}`);
    }
  }, 60000); // Check every minute

  // Monitor for blocked users
  setInterval(() => {
    const blockedKeys = rateLimiter.getBlockedKeys();
    if (blockedKeys.length > 0) {
      console.log(`Currently blocked keys: ${blockedKeys.length}`);
    }
  }, 30000); // Check every 30 seconds

  // Clear old violations periodically
  setInterval(() => {
    const violations = secureApiClient.getViolations();
    if (violations.length > 50) {
      secureApiClient.clearViolations();
      console.log('Cleared old security violations');
    }
  }, 5 * 60 * 1000); // Check every 5 minutes
}

/**
 * Clean up security services
 */
export function cleanupSecurity(): void {
  try {
    rateLimiter.destroy();
    errorMonitoringService.clearUserContext();
    console.log('✓ Security services cleaned up');
  } catch (error) {
    console.error('Failed to cleanup security services:', error);
  }
}